import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Typography, CircularProgress, Chip } from "@mui/material";
import {
  Groups as LiveIcon,
  LocalFireDepartment as HotSeatIcon,
} from "@mui/icons-material";
import io from "socket.io-client";
import LiveWheelDisplay from "../../Common/LiveWheelDisplay";
import { prizeIndexFromSpin } from "../../Common/DefaultStyleWheel";
import useGameSounds from "../../../hooks/useGameSounds";
import SoundMuteButton from "../../Common/SoundMuteButton";
import { SPIN, spinPageSx, spinPulse, spinTitleGradientSx } from "../pages/spinTheme";
import { SW_GAME_TYPES, SW_PHASES, isWheelVisiblePhase } from "../pages/spinHostPhases";
import { renderSpinHostPhaseView } from "../pages/SpinHostPhaseViews";
import { bindGameResync } from "../../../utils/bindGameResync";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const SpectatorSpinWheel = () => {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const entriesRef = useRef([]);
  entriesRef.current = entries;
  const [liveMode, setLiveMode] = useState("participants");
  const [hotSeatInfo, setHotSeatInfo] = useState(null);
  const [prizeWheelMeta, setPrizeWheelMeta] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [finalAngle, setFinalAngle] = useState(0);
  const [spinDuration, setSpinDuration] = useState(6);
  const [winner, setWinner] = useState(null);
  const [animComplete, setAnimComplete] = useState(false);
  const [prizeIndex, setPrizeIndex] = useState(0);
  const [title, setTitle] = useState("Users Wheel");
  const [error, setError] = useState(null);
  const [hostPhase, setHostPhase] = useState(null);
  const [hostGameType, setHostGameType] = useState(null);
  const [announceWheelData, setAnnounceWheelData] = useState(null);
  const isLiveRef = useRef(false);
  const liveModeRef = useRef("participants");
  const { play } = useGameSounds("spin_wheel");
  const playRef = useRef(play);

  useEffect(() => {
    playRef.current = play;
  }, [play]);

  useEffect(() => {
    isLiveRef.current =
      entries.length >= 2 && isWheelVisiblePhase(hostPhase || SW_PHASES.WHEEL_READY);
  }, [entries.length, hostPhase]);

  useEffect(() => {
    liveModeRef.current = liveMode;
  }, [liveMode]);

  const applyLiveData = useCallback(
    (data, { reconcileMotion = false } = {}) => {
      const entriesData = data.entries || [];
      const mode = data.liveMode || "participants";
      const phase =
        data.phase ||
        data.host?.phase ||
        (entriesData.length >= 2 ? SW_PHASES.WHEEL_READY : SW_PHASES.INTRO);
      const gameType =
        data.gameType ||
        data.host?.gameType ||
        (mode === "hot_seat" ? SW_GAME_TYPES.HOT_SEAT : SW_GAME_TYPES.USER_WHEEL);

      setHostPhase(phase);
      setHostGameType(gameType);
      setLiveMode(mode);
      setEntries(entriesData);

      if (data.announceWheel) setAnnounceWheelData(data.announceWheel);

      if (mode === "hot_seat") {
        setHotSeatInfo(data.hotSeat || data.host?.selectedHotSeat || null);
        setPrizeWheelMeta(data.prizeWheel || null);
        setTitle(data.prizeWheel?.wheel_name || data.session?.title || "Hot Seat");
      } else {
        setHotSeatInfo(null);
        setPrizeWheelMeta(null);
        setTitle(data.session?.title || "Users Wheel");
      }

      if (data.session?.winner_entry_id && !spinning) {
        const w = entriesData.find((e) => e.id === data.session.winner_entry_id);
        if (w) setWinner(w);
      }
      if (
        reconcileMotion &&
        phase !== SW_PHASES.SPINNING &&
        phase !== SW_PHASES.USER_WHEEL_READY
      ) {
        setSpinning(false);
      }
      setError(null);
    },
    [spinning]
  );

  const fetchWheel = useCallback(
    async ({ silent = false, reconcileMotion = false } = {}) => {
      try {
        const res = await fetch(`${API_BASE}/api/spinning-wheel/public/live-wheel`);
        if (!res.ok) {
          setError("No wheel active");
          setHostPhase(null);
          setEntries([]);
          setSpinning(false);
          if (!silent) setLoading(false);
          return;
        }
        const data = await res.json();
        applyLiveData(data, { reconcileMotion });
      } catch (err) {
        console.error("[SpectatorSpinWheel] fetch error:", err);
        setError("Failed to load wheel");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [applyLiveData]
  );

  useEffect(() => {
    fetchWheel();
  }, [fetchWheel]);

  useEffect(() => {
    const socket = io(API_BASE, { transports: ["websocket"] });
    socket.on("connect", () => {
      socket.emit("spinwheel:join-live");
      fetchWheel({ silent: true, reconcileMotion: true });
    });
    const unbindResync = bindGameResync(() => {
      if (socket.connected) socket.emit("spinwheel:join-live");
      fetchWheel({ silent: true, reconcileMotion: true });
    });

    socket.on("spinwheel:host_updated", (payload) => {
      if (payload?.host || payload?.phase) applyLiveData(payload);
      else fetchWheel({ silent: true });
    });
    socket.on("spinwheel:host_ended", () => fetchWheel({ silent: true }));
    socket.on("spinwheel:live_updated", () => fetchWheel({ silent: true }));
    socket.on("spinwheel:hot_seat_updated", () => fetchWheel({ silent: true }));

    const handleSpinStart = ({ finalAngle: fa, spinDuration: sd, winnerId, winnerIdx }) => {
      if (!isLiveRef.current) {
        fetchWheel({ silent: true });
        return;
      }
      setWinner(null);
      setAnimComplete(false);
      setFinalAngle(fa);
      setSpinDuration(sd);
      setPrizeIndex(prizeIndexFromSpin(winnerIdx, winnerId, entriesRef.current));
      setSpinning(true);
      const landed = entriesRef.current.find((e) => e.id === winnerId);
      if (landed) setTimeout(() => setWinner(landed), 0);
      playRef.current("spin_start");
    };

    socket.on("spinwheel:spin_start", handleSpinStart);
    socket.on("spinwheel:hot_seat_spin_start", handleSpinStart);

    socket.on("spinwheel:spin_complete", ({ session }) => {
      if (liveModeRef.current !== "participants") return;
      if (session?.entries) {
        setEntries(session.entries.filter((e) => !e.is_excluded));
      }
    });

    socket.on("spinwheel:hot_seat_spin_complete", ({ session }) => {
      if (liveModeRef.current !== "hot_seat") return;
      if (session?.entries) setEntries(session.entries);
    });

    socket.on("spinwheel:ended", () => {
      setSpinning(false);
      setAnimComplete(false);
      setWinner(null);
      fetchWheel({ silent: true });
    });

    socket.on("spinwheel:hot_seat_ended", () => {
      setSpinning(false);
      setAnimComplete(false);
      setWinner(null);
      fetchWheel({ silent: true });
    });

    const poll = setInterval(() => fetchWheel({ silent: true }), 15000);

    return () => {
      unbindResync();
      clearInterval(poll);
      socket.emit("spinwheel:leave-live");
      socket.disconnect();
    };
  }, [fetchWheel, applyLiveData]);

  const handleAnimEnd = useCallback(() => {
    setSpinning(false);
    setAnimComplete(true);
  }, []);

  const isHotSeat = liveMode === "hot_seat";
  const hotName = hotSeatInfo?.display_name || "";
  const centerLogo = isHotSeat ? "/mind_your_music.png" : prizeWheelMeta?.logo_url || "/mind_your_music.png";

  if (loading) {
    return (
      <Box sx={spinPageSx}>
        <SoundMuteButton floating />
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            zIndex: 1,
          }}
        >
          <CircularProgress sx={{ color: SPIN.gold }} />
          <Typography sx={{ color: SPIN.muted }}>Finding the live wheel…</Typography>
        </Box>
      </Box>
    );
  }

  if (error && !hostPhase) {
    return (
      <Box sx={spinPageSx}>
        <SoundMuteButton floating />
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
            px: 2,
          }}
        >
          <Typography sx={{ color: SPIN.muted, fontSize: "1.1rem", textAlign: "center" }}>
            {error}
          </Typography>
        </Box>
      </Box>
    );
  }

  const phaseView = renderSpinHostPhaseView({
    phase: hostPhase,
    gameType: hostGameType || (isHotSeat ? SW_GAME_TYPES.HOT_SEAT : SW_GAME_TYPES.USER_WHEEL),
    hotSeatName: hotName,
    announceWheel: announceWheelData,
  });
  if (phaseView) {
    return (
      <Box sx={spinPageSx}>
        <SoundMuteButton floating />
        {phaseView}
      </Box>
    );
  }

  if (!isWheelVisiblePhase(hostPhase) || entries.length < 2) {
    return (
      <Box sx={spinPageSx}>
        <SoundMuteButton floating />
        {renderSpinHostPhaseView({
          phase: SW_PHASES.BUILDING_WHEEL,
          gameType: hostGameType,
          hotSeatName: hotName,
          announceWheel: announceWheelData,
        })}
      </Box>
    );
  }

  return (
    <Box sx={spinPageSx}>
      <SoundMuteButton floating />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 2,
          px: 2,
          zIndex: 1,
        }}
      >
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Typography
            sx={{
              ...spinTitleGradientSx,
              fontSize: { xs: "1.3rem", md: "1.8rem" },
              mb: 1,
              whiteSpace: "normal",
            }}
          >
            {title}
          </Typography>
          <Chip
            label={isHotSeat ? "HOT SEAT" : "USERS WHEEL"}
            size="small"
            icon={
              isHotSeat ? (
                <HotSeatIcon sx={{ fontSize: "14px !important" }} />
              ) : (
                <LiveIcon sx={{ fontSize: "14px !important" }} />
              )
            }
            sx={{
              bgcolor: isHotSeat ? "rgba(255,107,61,0.2)" : "rgba(255,45,138,0.2)",
              color: isHotSeat ? SPIN.hot : SPIN.live,
              fontWeight: 800,
              mb: 1,
              border: `1px solid ${isHotSeat ? SPIN.hot : SPIN.live}55`,
              "& .MuiChip-icon": { color: isHotSeat ? SPIN.hot : SPIN.live },
              animation: `${spinPulse} 2.2s ease-in-out infinite`,
            }}
          />
          {isHotSeat && hotName && (
            <Typography
              sx={{
                color: SPIN.hot,
                fontWeight: 800,
                fontSize: { xs: "1.1rem", md: "1.4rem" },
                mt: 1,
              }}
            >
              Hot seat: {hotName}
            </Typography>
          )}
        </Box>

        <LiveWheelDisplay
          entries={entries}
          spinning={spinning}
          finalAngle={finalAngle}
          spinDuration={spinDuration}
          winnerEntry={winner}
          animationComplete={animComplete}
          onAnimationEnd={handleAnimEnd}
          size={
            isHotSeat
              ? Math.max(220, Math.min(window.innerWidth - 48, window.innerHeight - 280, 445))
              : Math.min(window.innerWidth - 40, 500)
          }
          centerLogoSrc={centerLogo}
          hotSeatPlayerName={isHotSeat ? hotName : null}
          backgroundImageUrl={isHotSeat ? prizeWheelMeta?.background_image_url || "" : ""}
          onWinnerReveal={() => playRef.current("winner")}
          variant={isHotSeat ? "default" : "live"}
          prizeIndex={prizeIndex}
        />
      </Box>
    </Box>
  );
};

export default SpectatorSpinWheel;
